import { useEffect, useMemo, useRef } from 'react';
import { Modal, Typography } from '@uniquegood/realworld-studio-design';
import { css } from '@emotion/react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { ClickEventName, PageViewEventName, PromotionTriggerLocation, track } from '@/track';

import { onMobile } from '@/styles/responsive';

import LoadingSpinner from '@/components/LoadingSpinner';
import {
  useIsAdminSelector,
  useIsOwner,
  useModalState,
  useRequest,
  useUser,
  usePromotionModal,
  useUserRole,
  useChannelLimit
} from '@/hooks';
import ChannelLimitGuard from '@/components/ChannelLimitGuard';
import Sidebar from './components/Sidebar';
import ProjectList from './sections/ProjectList';
import Feeds from './sections/Feeds';
import Community from './sections/Community';
import AiBanner from '@/components/AiBanner';
import Settings from '../settings';
import SubSidebar from './components/SubSidebar';
import Channel from './sections/Settings/components/Channel';
import Members from './sections/Settings/components/Members';
import Plan from './sections/Settings/components/Plan';
import Donations from './sections/Settings/components/Donations';
import { Container650 } from '@/components/containers';
import useDowngrade from './hooks/useDowngrade';
import Footer from '@/components/SharedFooter';

type SelectedMenuItem = 'home' | 'feeds' | 'community' | 'settings';
export type SettingsMenuType = 'channel' | 'members' | 'donations' | 'plan';

function Home() {
  const { data: user } = useUser();
  const { data: isAdmin } = useIsAdminSelector();
  const { data: isOwner } = useIsOwner();
  const { modal, openModal, closeModal } = useModalState();
  const { openPromotionModal: openPromotionModalHook } = usePromotionModal();

  const history = useHistory();

  const { appId } = useParams<AppParam>();
  const { data: app, mutate } = useRequest<RealWorldApp>(`/apps/${appId}`);
  const { data: me } = useRequest<MyInfo>('/api/me');
  const { data: applicationInfo, mutate: mutateApplicationInfo } = useRequest<
    CommonResponseModel<ApplicationInfoResponseModel>
  >(`/apps/${appId}/info`);

  const location = useLocation();

  const { checkLimitAndShowModal, modal: limitModal } = useChannelLimit();

  useEffect(() => {
    if (!appId) {
      return;
    }

    checkLimitAndShowModal('콘텐츠');
  }, [appId]);

  const SUGGESTED_PRICING_PLAN: Record<UsePurposeType, 'Pro' | 'Enterprise' | 'Class'> = {
    Personal: 'Pro',
    Campaign: 'Enterprise',
    Education: 'Class',
    Etc: 'Pro'
  };

  const openPromotionModal = (option?: 'channel_create' | 'downgrade') => {
    openPromotionModalHook({
      recommendedPlan: SUGGESTED_PRICING_PLAN[purpose],
      triggerLocation:
        option === 'channel_create'
          ? PromotionTriggerLocation.channel_create
          : PromotionTriggerLocation.plan_downgraded
    });
  };

  const { downgradeModal } = useDowngrade({
    onOpenPromotionModal: () => openPromotionModal('downgrade')
  });

  const selectedMenuItem = useMemo(
    () => (new URLSearchParams(history.location.search).get('tab') ?? 'home') as SelectedMenuItem,
    [history.location.search]
  );
  const selectedSubMenuItem = useMemo(
    () =>
      (new URLSearchParams(history.location.search).get('subTab') ?? 'channel') as SettingsMenuType,
    [history.location.search]
  );

  const { isEditor, isTester } = useUserRole();

  // Editor, Tester는 settings 탭 접근 불가 - 홈으로 리다이렉트
  useEffect(() => {
    if ((isEditor || isTester) && selectedMenuItem === 'settings') {
      history.replace(`/apps/${appId}`);
    }
  }, [isEditor, isTester, selectedMenuItem, appId, history]);

  const { purpose, onboardingCompleted, proSubscribeSuccess, trialRedeemSuccess } =
    (location.state as {
      purpose: UsePurposeType;
      onboardingCompleted: boolean;
      proSubscribeSuccess: boolean;
      trialRedeemSuccess: boolean;
    }) || {};

  useEffect(() => {
    if (proSubscribeSuccess) {
      openModal({
        title: '프로 요금제 결제가 완료됐어요!',
        children: (
          <>
            <div>
              <Typography
                type="body"
                as="span"
                cssStyle={css`
                  color: #7800ff;
                `}
              >
                {app?.name || applicationInfo?.data?.name || ''}
              </Typography>
              <Typography type="body" as="span">
                에 프로 요금제가 적용됐어요.
              </Typography>
            </div>
            <Typography type="body">{`마음껏 ${me?.name}님의 아이디어를 펼쳐보세요!`}</Typography>
          </>
        ),
        primaryAction: {
          content: '확인',
          onAction: () => {
            closeModal();
          }
        }
      });
    }
  }, [proSubscribeSuccess, app, me, applicationInfo]);

  useEffect(() => {
    if (trialRedeemSuccess) {
      openModal({
        title: '무료 체험이 시작되었어요!',
        children: (
          <>
            <div>
              <Typography
                type="body"
                as="span"
                cssStyle={css`
                  color: #7800ff;
                `}
              >
                {app?.name || applicationInfo?.data?.name || ''}
              </Typography>
              <Typography type="body" as="span">
                에 요금제가 적용됐어요.
              </Typography>
            </div>
            <Typography type="body">{`마음껏 ${me?.name}님의 아이디어를 펼쳐보세요!`}</Typography>
          </>
        ),
        primaryAction: {
          content: '확인',
          onAction: () => {
            closeModal();
          }
        }
      });
    }
  }, [trialRedeemSuccess, app, me, applicationInfo]);

  useEffect(() => {
    if (onboardingCompleted && purpose) {
      openPromotionModal('channel_create');
    }
  }, [onboardingCompleted, purpose]);

  const openAiGeneratingAlertModal = () => {
    openModal({
      title: '콘텐츠를 만들고 있어요.',
      onClose: () => {
        sessionStorage.removeItem('aiGenerationComplete');
      },
      primaryAction: {
        content: '닫기',
        onAction: () => {
          closeModal();
          sessionStorage.removeItem('aiGenerationComplete');
        }
      },
      children: (
        <>
          <Typography as="p" type="body">
            AI 만들기는
            <Typography
              as="span"
              type="body"
              cssStyle={css`
                font-weight: 700;
              `}
            >
              {' '}
              약 10분 소요
            </Typography>
            됩니다.
            <br />
            진행 사항은
            <Typography
              as="span"
              type="body"
              cssStyle={css`
                font-weight: 700;
              `}
            >
              {' '}
              새로고침
            </Typography>
            하여 확인할 수 있고, 스토리와 이미지 제작이 완료되면
            <br />
            콘텐츠를 확인할 수 있어요.
          </Typography>
        </>
      )
    });
  };

  // AI 생성 완료 상태 확인 및 모달 표시
  useEffect(() => {
    const aiGenerationComplete = sessionStorage.getItem('aiGenerationComplete');

    if (aiGenerationComplete === 'true') {
      openAiGeneratingAlertModal();
    }
  }, []);

  const viewHomeEvent = async () => {
    let applicationInfoData = applicationInfo;

    if (!applicationInfoData) {
      applicationInfoData = await mutateApplicationInfo();
    }

    const param = {
      channel_id: applicationInfoData?.data.id,
      channel_name: applicationInfoData?.data.name ?? '',
      plan_type: applicationInfoData?.data.planType,
      member_level: user?.level ?? '',
      use_purpose_type: applicationInfoData?.data.usePurposeType ?? '',
      user_id: user?.id
    };

    track.onPageView({
      pageViewEventName: PageViewEventName.view_channel_createdgames,
      params: param
    });
  };

  const hasTrackedHomeView = useRef(false);

  useEffect(() => {
    if (user?.level === undefined || selectedMenuItem !== 'home') {
      return;
    }

    if (hasTrackedHomeView.current) {
      return;
    }

    hasTrackedHomeView.current = true;
    viewHomeEvent();
  }, [appId, selectedMenuItem, user?.level]);

  // 탭 또는 appId 변경 시 트래킹 플래그 리셋
  useEffect(() => {
    hasTrackedHomeView.current = false;
  }, [appId, selectedMenuItem]);

  useEffect(() => {
    switch (selectedMenuItem) {
      case 'feeds':
        track.onPageView({ pageViewEventName: PageViewEventName.view_channel_feed });
        break;
      case 'community':
        track.onPageView({ pageViewEventName: PageViewEventName.view_community });
        break;
      case 'settings':
        track.onPageView({ pageViewEventName: PageViewEventName.view_channel_setting });
        switch (selectedSubMenuItem) {
          case 'channel':
            track.onPageView({ pageViewEventName: PageViewEventName.view_setting_channel });
            break;
          case 'members':
            track.onPageView({ pageViewEventName: PageViewEventName.view_setting_member });
            break;
          case 'plan':
            track.onPageView({ pageViewEventName: PageViewEventName.view_setting_plan });
            break;
          case 'donations':
            track.onPageView({ pageViewEventName: PageViewEventName.view_setting_donation });
            break;
          default:
            break;
        }
        break;
      default:
        break;
    }
  }, [selectedMenuItem]);

  useEffect(() => {
    switch (selectedSubMenuItem) {
      case 'channel':
        track.onPageView({ pageViewEventName: PageViewEventName.view_setting_channel });
        break;
      case 'members':
        track.onPageView({ pageViewEventName: PageViewEventName.view_setting_member });
        break;
      case 'plan':
        track.onPageView({ pageViewEventName: PageViewEventName.view_setting_plan });
        break;
      case 'donations':
        track.onPageView({ pageViewEventName: PageViewEventName.view_setting_donation });
        break;
      default:
        break;
    }
  }, [selectedSubMenuItem]);

  if (isAdmin === undefined || isOwner === undefined) return <LoadingSpinner />;

  const selectedContent: Record<SelectedMenuItem, JSX.Element> = {
    home: <ProjectList />,
    feeds: (
      <ChannelLimitGuard targetName="피드">
        <Feeds />
      </ChannelLimitGuard>
    ),
    community: (
      <ChannelLimitGuard targetName="커뮤니티">
        <Community />
      </ChannelLimitGuard>
    ),
    settings: <Settings />
  };

  const selectedSettingsContent: Record<SettingsMenuType, JSX.Element> = {
    channel: <Channel appName={app?.name ?? ''} appDescription={app?.description ?? ''} />,
    members: <Members />,
    donations: <Donations />,
    plan: <Plan />
  };

  const handleSelectMenu = (menu: SettingsMenuType) => {
    history.push(`/apps/${appId}?tab=settings&subTab=${menu}`);
  };

  // 메뉴 변경 시 스크롤을 맨 위로 즉시 이동 (애니메이션 없음)
  useEffect(() => {
    // 스크롤 위치가 이미 상단에 가까우면 실행하지 않음
    if (window.scrollY < 100) {
      return;
    }
    // 라우팅 완료 후 다음 프레임에서 실행하여 부드럽게 처리
    requestAnimationFrame(() => {
      window.scrollTo({ top: 0, behavior: 'auto' });
    });
  }, [selectedMenuItem, selectedSubMenuItem]);

  return (
    <div css={RootContainer}>
      {/* 띠배너 추가 시 page layout 위에 추가: <BeltBanner backgroundColor="#HEX" href="링크">내용</BeltBanner> */}
      <div css={PageLayout}>
        <Sidebar selectedMenu={selectedMenuItem === 'home' ? 'home' : selectedMenuItem} />
        {selectedMenuItem === 'settings' && (
          <div css={SubSidebarStyle}>
            <SubSidebar onSelectMenu={handleSelectMenu} selectedMenu={selectedSubMenuItem} />
          </div>
        )}
        <div css={MainContentArea}>
          {/* <Header showSettingButton={isOwner || isAdmin} /> */}
          <div css={ContentContainer}>
            {selectedMenuItem === 'home' && (
              <AiBanner
                onClick={() => {
                  track.onClick({
                    clickEventName: ClickEventName.click_channel_banner_ai,
                    params: { appId }
                  });
                  window.open(
                    'https://uniquegoodcompany.notion.site/AI-26418b2eed608024ab98d04753a9efd0',
                    '_blank'
                  );
                }}
              />
            )}
            {selectedMenuItem === 'settings' ? (
              <div css={SubSidebarStyle}>
                <Container650>{selectedSettingsContent[selectedSubMenuItem]}</Container650>
              </div>
            ) : (
              selectedContent[selectedMenuItem]
            )}
          </div>
        </div>
      </div>
      <Footer />
      <Modal {...modal} />
      <Modal {...downgradeModal} />
      <Modal {...limitModal} />
    </div>
  );
}

export default Home;

const RootContainer = css`
  display: flex;
  flex-direction: column;
  min-height: 100vh;

  ${onMobile} {
    height: auto;
    overflow: visible;
  }
`;

const SubSidebarStyle = css`
  animation: slideInFromLeft 0.3s ease-in-out;

  @keyframes slideInFromLeft {
    from {
      transform: translateX(-20px);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
`;

const PageLayout = css`
  display: flex;
  width: 100%;
  flex: 1;

  ${onMobile} {
    flex-direction: column;
  }
`;

const MainContentArea = css`
  flex: 1;
  display: flex;
  flex-direction: column;
  width: 100%;

  ${onMobile} {
    max-width: 100%;
  }
`;

const ContentContainer = css`
  padding: 32px;
  margin-bottom: 80px;
  width: 960px;
  margin-left: auto;
  margin-right: auto;

  ${onMobile} {
    padding: 16px;
    margin-bottom: 0px;
    width: 100%;
  }
`;

